import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import './AssistantConversations.css';

const fmtInt = (n) => (Number(n) || 0).toLocaleString();
const fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDay = (d) => {
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? d : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const StatCard = ({ label, value, sub }) => (
  <div className="stat-card">
    <div className="stat-label">{label}</div>
    <div className="stat-value" style={{ fontSize: '28px' }}>{value}</div>
    {sub != null && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>{sub}</div>}
  </div>
);

const Section = ({ title, children, right }) => (
  <div className="card" style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <h2 className="card-title" style={{ margin: 0 }}>{title}</h2>
      {right}
    </div>
    {children}
  </div>
);

const BAR_COLOR = '#3b82f6';

const FREQ = {
  high: { label: 'часто', bg: '#fee2e2', color: '#b91c1c' },
  medium: { label: 'средне', bg: '#fef3c7', color: '#92400e' },
  low: { label: 'редко', bg: '#e5e7eb', color: '#374151' },
};
const FreqBadge = ({ frequency }) => {
  const f = FREQ[frequency] || FREQ.medium;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, background: f.bg, color: f.color, borderRadius: 999, padding: '2px 8px' }}>
      {f.label}
    </span>
  );
};

// Turns an opportunity card into a self-contained prompt for an external chat
// (ChatGPT/Claude): full app context + feature spec + user evidence, ending with
// a request for an interactive HTML mockup so the feature can be seen visually.
const buildDesignPrompt = (o) => {
  const freq = FREQ[o.frequency]?.label || '';
  const lines = [];
  lines.push('Ты — senior product designer. Спроектируй новую фичу для iOS-приложения KKAL — трекера питания и калорий (дневник еды, цели по калориям и БЖУ, режим интервального голодания / fasting, встроенный AI-ассистент по питанию).');
  lines.push('');
  lines.push(`Фича: ${o.feature || o.title || ''}`);
  if (o.feature && o.title && o.feature !== o.title) lines.push(`Суть: ${o.title}`);
  if (o.description) {
    const source = [
      o.userCount > 0 ? `${o.userCount} юзеров` : null,
      freq ? `спрашивают ${freq}` : null,
    ].filter(Boolean).join(', ');
    lines.push('');
    lines.push(`Контекст из анализа реальных чатов пользователей${source ? ` (${source})` : ''}:`);
    lines.push(o.description);
  }
  if (o.placement) {
    lines.push('');
    lines.push(`Где в приложении: ${o.placement}`);
  }
  if (o.actionItems?.length) {
    lines.push('');
    lines.push('Что фича должна уметь:');
    o.actionItems.forEach((step, i) => lines.push(`${i + 1}. ${step}`));
  }
  if (o.evidence?.length) {
    lines.push('');
    lines.push('Реальные запросы пользователей:');
    o.evidence.forEach((ex) => lines.push(`— «${ex}»`));
  }
  lines.push('');
  lines.push('Задача: сделай интерактивный HTML-прототип этой фичи (один самодостаточный файл, мобильный вьюпорт 390×844, нативный iOS-стиль, светлая тема), чтобы было понятно, как фича будет выглядеть визуально: все экраны и состояния, реальные тексты кнопок и подписей, правдоподобные примеры данных. Покажи полный сценарий использования — от точки входа в фичу до результата.');
  return lines.join('\n');
};

const AssistantInsights = () => {
  const { fetchWithAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedPromptIdx, setCopiedPromptIdx] = useState(null);

  const [analyses, setAnalyses] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [running, setRunning] = useState(false);
  const [runStage, setRunStage] = useState(null); // 'starting' | 'llm'
  const [elapsedSec, setElapsedSec] = useState(0);
  const [analysisError, setAnalysisError] = useState(null);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchWithAuth('/api/admin/assistant/insights?days=30');
      if (!resp.ok) throw new Error('Failed to load insights');
      setData(await resp.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => { loadInsights(); }, [loadInsights]);

  // Load saved analyses on mount: history list + latest completed result.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [listResp, latestResp] = await Promise.all([
          fetchWithAuth('/api/admin/assistant/analyses?limit=20'),
          fetchWithAuth('/api/admin/assistant/analyses/latest'),
        ]);
        if (cancelled) return;
        if (listResp.ok) {
          const body = await listResp.json();
          setAnalyses(body.data || []);
        }
        if (latestResp.ok) {
          const latest = await latestResp.json();
          if (latest) setAnalysis(latest);
        }
      } catch (err) {
        // History is non-critical on load; the run button reports real errors.
      }
    })();
    return () => { cancelled = true; };
  }, [fetchWithAuth]);

  // POST returns a 'running' row immediately (the OpenAI call happens in the
  // background on the server — a long HTTP request would hit proxy timeouts),
  // then we poll the row until it settles.
  const runAnalysis = useCallback(async () => {
    setRunning(true);
    setRunStage('starting');
    setAnalysisError(null);
    const t0 = Date.now();
    setElapsedSec(0);
    const timer = setInterval(() => setElapsedSec(Math.floor((Date.now() - t0) / 1000)), 1000);
    try {
      const resp = await fetchWithAuth('/api/admin/assistant/analyses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await resp.json();
      if (!resp.ok) throw new Error(body.error || 'Failed to run analysis');
      setRunStage('llm');

      const id = body.id;
      const startedAt = Date.now();
      const MAX_WAIT_MS = 6 * 60 * 1000;
      while (Date.now() - startedAt < MAX_WAIT_MS) {
        await new Promise((r) => setTimeout(r, 4000));
        let row;
        try {
          const poll = await fetchWithAuth(`/api/admin/assistant/analyses/${id}`);
          if (!poll.ok) continue;
          row = await poll.json();
        } catch (e) {
          continue; // transient network error — keep polling
        }
        if (row.status === 'completed') {
          setAnalysis(row);
          setAnalyses((prev) => [row, ...prev.filter((a) => a.id !== row.id)]);
          return;
        }
        if (row.status === 'error') {
          setAnalyses((prev) => [row, ...prev.filter((a) => a.id !== row.id)]);
          throw new Error(row.error || 'Analysis failed');
        }
      }
      throw new Error('Analysis is taking too long — reload the page in a minute, the result will appear in history.');
    } catch (err) {
      setAnalysisError(err.message);
    } finally {
      clearInterval(timer);
      setRunning(false);
      setRunStage(null);
    }
  }, [fetchWithAuth]);

  const copyDesignPrompt = useCallback(async (o, i) => {
    try {
      await navigator.clipboard.writeText(buildDesignPrompt(o));
      setCopiedPromptIdx(i);
      setTimeout(() => setCopiedPromptIdx(null), 1500);
    } catch (_) {
      // clipboard unavailable (non-HTTPS) — nothing useful to show
    }
  }, []);

  const selectAnalysis = useCallback(async (id) => {
    if (!id) return;
    setAnalysisError(null);
    try {
      const resp = await fetchWithAuth(`/api/admin/assistant/analyses/${id}`);
      const body = await resp.json();
      if (!resp.ok) throw new Error(body.error || 'Failed to load analysis');
      setAnalysis(body);
    } catch (err) {
      setAnalysisError(err.message);
    }
  }, [fetchWithAuth]);

  if (loading) return <div className="loading">Loading insights...</div>;
  if (error) return <div className="error" style={{ color: '#b91c1c' }}>Error: {error}</div>;

  const totals = data?.totals;
  const models = data?.models || [];
  const totalCost = models.reduce((acc, m) => acc + (m.estCostUsd || 0), 0);
  const totalTokens = models.reduce((acc, m) => acc + (m.totalTokens || 0), 0);
  const keywords = data?.keywords || [];
  const maxKw = keywords.length ? keywords[0].count : 1;

  const result = analysis?.result;
  const maxThemeShare = result?.themes?.length
    ? Math.max(...result.themes.map((t) => t.sharePercent || 0), 1)
    : 1;

  const fmtRunDate = (d) => {
    const date = new Date(d);
    return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleString('ru-RU', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '4px' }}>AI Chats — Insights</h1>
      <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 8 }}>
        What users ask the assistant — and what to build next from it.
      </p>
      <div style={{ marginBottom: 20 }}>
        <Link className="link" to="/assistant">← Back to conversations</Link>
      </div>

      {/* Totals */}
      {totals && (
        <div className="grid grid-4" style={{ marginBottom: 20 }}>
          <StatCard label="Conversations" value={fmtInt(totals.conversations)} />
          <StatCard label="Messages" value={fmtInt(totals.messages)} sub={`${fmtInt(totals.userMessages)} from users`} />
          <StatCard label="Users with chats" value={fmtInt(totals.users)} />
          <StatCard
            label="Avg messages / chat"
            value={(totals.avgMessagesPerConversation || 0).toFixed(1)}
          />
        </div>
      )}

      {/* Themes + product opportunities (LLM) */}
      <Section
        title="What to build next (AI analysis of user chats)"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {analyses.length > 0 && (
              <select
                value={analysis?.id || ''}
                onChange={(e) => selectAnalysis(e.target.value)}
                disabled={running}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
              >
                {!analysis && <option value="">История анализов…</option>}
                {analyses.map((a) => (
                  <option key={a.id} value={a.id}>
                    {fmtRunDate(a.createdAt)} · {fmtInt(a.sampleConversations)} чатов · {a.model}
                    {a.status !== 'completed' ? ` · ${a.status}` : ''}
                  </option>
                ))}
              </select>
            )}
            <button
              className="btn btn-primary"
              onClick={runAnalysis}
              disabled={running}
            >
              {running
                ? `Анализируем… ${Math.floor(elapsedSec / 60)}:${String(elapsedSec % 60).padStart(2, '0')}`
                : 'Новый анализ'}
            </button>
          </div>
        }
      >
        {running && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
            padding: '10px 14px', marginBottom: 16, fontSize: 14, color: '#1e3a8a',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="spinner" style={{
                width: 14, height: 14, border: '2px solid #bfdbfe', borderTopColor: '#3b82f6',
                borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite',
              }} />
              {runStage === 'starting'
                ? 'Шаг 1/2 — собираем сообщения юзеров из последних чатов…'
                : 'Шаг 2/2 — модель читает диалоги и формирует выводы (обычно 1–3 минуты)…'}
            </div>
            <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
            <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 6 }}>
              Прошло {Math.floor(elapsedSec / 60)}:{String(elapsedSec % 60).padStart(2, '0')}.
              Страницу можно закрыть — результат сохранится в историю.
            </div>
          </div>
        )}
        {analysisError && <div style={{ color: '#b91c1c', marginBottom: 12 }}>Error: {analysisError}</div>}
        {!analysis && !running && !analysisError && (
          <div style={{ color: '#6b7280' }}>
            Нажми «Новый анализ» — AI прочитает все сообщения юзеров из последних чатов
            (армянский / русский / английский, тестовые юзеры исключены), сгруппирует их в темы
            и предложит конкретные фичи для приложения. Каждый анализ сохраняется в историю.
          </div>
        )}
        {analysis?.status === 'error' && (
          <div style={{ color: '#b91c1c', marginBottom: 12 }}>
            Этот запуск завершился ошибкой: {analysis.error || 'unknown error'}
          </div>
        )}
        {result && (
          <div>
            {result.summary && (
              <p style={{ color: '#374151', marginTop: 0 }}>{result.summary}</p>
            )}
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>
              {fmtRunDate(analysis.createdAt)} · {fmtInt(analysis.sampleConversations)} чатов,{' '}
              {fmtInt(analysis.sampleMessages)} сообщений · {analysis.model}
              {analysis.durationMs != null ? ` · ${Math.round(analysis.durationMs / 1000)}s` : ''}
              {analysis.estCostUsd != null ? ` · ${fmtUsd(analysis.estCostUsd)}` : ''}
            </div>

            {/* Product opportunities — the actionable output */}
            {(result.opportunities || []).length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>💡 Что добавить в приложение</h3>
                {result.opportunities.map((o, i) => {
                  const freqColor = o.frequency === 'high' ? '#dc2626' : o.frequency === 'low' ? '#9ca3af' : '#f59e0b';
                  return (
                    <div
                      key={i}
                      style={{
                        border: '1px solid #e5e7eb', borderLeft: `4px solid ${freqColor}`,
                        borderRadius: 10, padding: '14px 16px', marginBottom: 12, background: '#fff',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{
                          width: 24, height: 24, borderRadius: '50%', background: freqColor, color: '#fff',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, fontWeight: 700, flexShrink: 0,
                        }}>
                          {i + 1}
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{o.feature || o.title}</span>
                        <FreqBadge frequency={o.frequency} />
                        {o.userCount > 0 && (
                          <span style={{
                            fontSize: 11, fontWeight: 600, background: '#eff6ff', color: '#1d4ed8',
                            borderRadius: 999, padding: '2px 8px',
                          }}>
                            👥 {o.userCount} {o.userCount === 1 ? 'юзер' : o.userCount < 5 ? 'юзера' : 'юзеров'}
                          </span>
                        )}
                        <button
                          onClick={() => copyDesignPrompt(o, i)}
                          title="Скопировать готовый промпт — отдай его чату (ChatGPT/Claude), чтобы он сделал дизайн этой фичи"
                          style={{
                            marginLeft: 'auto', flexShrink: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                            fontSize: 12.5, fontWeight: 600, padding: '4px 10px', borderRadius: 8,
                            border: copiedPromptIdx === i ? '1px solid #10b981' : '1px solid #d1d5db',
                            background: copiedPromptIdx === i ? '#ecfdf5' : '#fff',
                            color: copiedPromptIdx === i ? '#047857' : '#374151',
                          }}
                        >
                          {copiedPromptIdx === i ? '✓ Скопировано' : '🎨 Промпт для дизайна'}
                        </button>
                      </div>
                      {o.feature && o.title && o.feature !== o.title && (
                        <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 6 }}>{o.title}</div>
                      )}
                      {o.description && (
                        <div style={{ color: '#374151', fontSize: 14, marginBottom: 8 }}>{o.description}</div>
                      )}
                      {o.placement && (
                        <div style={{ fontSize: 13, color: '#1e3a8a', marginBottom: 8 }}>
                          📍 <strong>Куда:</strong> {o.placement}
                        </div>
                      )}
                      {o.actionItems?.length > 0 && (
                        <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Что сделать
                          </div>
                          {o.actionItems.map((step, j) => (
                            <div key={j} style={{ display: 'flex', gap: 8, fontSize: 13.5, color: '#374151', marginBottom: 4 }}>
                              <span style={{ color: '#10b981', fontWeight: 700 }}>{j + 1}.</span>
                              <span>{step}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {o.evidence?.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          {o.evidence.map((ex, j) => (
                            <div
                              key={j}
                              style={{
                                borderLeft: '3px solid #e5e7eb', paddingLeft: 10, margin: '4px 0',
                                color: '#6b7280', fontSize: 13, fontStyle: 'italic',
                              }}
                            >
                              «{ex}»
                            </div>
                          ))}
                        </div>
                      )}
                      {o.conversations?.length > 0 && (
                        <div style={{ marginTop: 8, fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          <span style={{ color: '#6b7280' }}>Чаты:</span>
                          {o.conversations.slice(0, 8).map((c) => (
                            <Link
                              key={c.id}
                              className="link"
                              to={`/assistant/conversations/${c.id}`}
                              style={{
                                background: '#f3f4f6', borderRadius: 6, padding: '1px 8px',
                                fontSize: 12.5, textDecoration: 'none',
                              }}
                            >
                              #{c.id}
                            </Link>
                          ))}
                          {o.conversations.length > 8 && (
                            <span style={{ color: '#9ca3af', fontSize: 12.5 }}>
                              +{o.conversations.length - 8} ещё
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Themes — supporting breakdown of what users ask about */}
            {(result.themes || []).length > 0 && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>What people ask about</h3>
                {result.themes.map((t, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{t.name}</span>
                      <span style={{ color: '#6b7280' }}>{Math.round(t.sharePercent)}%{t.count ? ` · ~${fmtInt(t.count)}` : ''}</span>
                    </div>
                    <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.max(2, (t.sharePercent / maxThemeShare) * 100)}%`, height: '100%', background: BAR_COLOR }} />
                    </div>
                    {t.examples?.length > 0 && (
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#6b7280', fontSize: 13 }}>
                        {t.examples.map((ex, j) => <li key={j}>{ex}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Volume over time */}
      <Section title="Activity over time (last 30 days)">
        {data?.daily?.length ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="day" tickFormatter={fmtDay} fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip labelFormatter={fmtDay} />
              <Area type="monotone" dataKey="messages" name="User messages" stroke="#3b82f6" fill="#bfdbfe" />
              <Area type="monotone" dataKey="conversations" name="New chats" stroke="#10b981" fill="#a7f3d0" />
            </AreaChart>
          </ResponsiveContainer>
        ) : <div style={{ color: '#6b7280' }}>No data.</div>}
      </Section>

      {/* Keywords */}
      <Section title="Top keywords in user messages">
        {keywords.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {keywords.map((k) => {
              const size = 12 + Math.round((k.count / maxKw) * 18);
              return (
                <span
                  key={k.word}
                  title={`${k.count} occurrences`}
                  style={{
                    fontSize: size,
                    lineHeight: 1.2,
                    color: '#1e3a8a',
                    background: '#eff6ff',
                    borderRadius: 999,
                    padding: '2px 10px',
                  }}
                >
                  {k.word}
                </span>
              );
            })}
          </div>
        ) : <div style={{ color: '#6b7280' }}>No data.</div>}
      </Section>

      {/* Day of week + hour */}
      <div className="grid grid-2" style={{ marginBottom: 0 }}>
        <Section title="By day of week">
          {data?.byDayOfWeek?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.byDayOfWeek}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="day" fontSize={12} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Bar dataKey="messages" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{ color: '#6b7280' }}>No data.</div>}
        </Section>

        <Section title="By hour of day">
          {data?.byHour?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.byHour}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="hour" fontSize={11} interval={1} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip labelFormatter={(h) => `${h}:00`} />
                <Bar dataKey="messages" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{ color: '#6b7280' }}>No data.</div>}
        </Section>
      </div>

      {/* Conversation length distribution */}
      <Section title="Conversation length (messages per chat)">
        {data?.lengthDistribution?.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.lengthDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="bucket" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Bar dataKey="conversations" radius={[4, 4, 0, 0]}>
                {data.lengthDistribution.map((_, i) => <Cell key={i} fill={i === 0 ? '#f59e0b' : BAR_COLOR} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <div style={{ color: '#6b7280' }}>No data.</div>}
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
          The first bar (single-message chats) are people who asked once and left.
        </div>
      </Section>

      {/* Models, tokens, cost */}
      <Section title="Models, tokens & estimated cost">
        <div className="grid grid-3" style={{ marginBottom: 16 }}>
          <StatCard label="Total tokens" value={fmtInt(totalTokens)} />
          <StatCard label="Est. cost" value={fmtUsd(totalCost)} sub="rough, based on public per-token pricing" />
          <StatCard label="Truncated replies" value={fmtInt(data?.truncatedReplies || 0)} sub="finish_reason = length" />
        </div>
        {models.length ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Replies</th>
                  <th>Prompt tok.</th>
                  <th>Completion tok.</th>
                  <th>Total tok.</th>
                  <th>Avg latency</th>
                  <th>Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.model}>
                    <td><strong>{m.model}</strong></td>
                    <td>{fmtInt(m.messages)}</td>
                    <td>{fmtInt(m.promptTokens)}</td>
                    <td>{fmtInt(m.completionTokens)}</td>
                    <td>{fmtInt(m.totalTokens)}</td>
                    <td>{m.avgLatencyMs != null ? `${fmtInt(m.avgLatencyMs)} ms` : '—'}</td>
                    <td>{fmtUsd(m.estCostUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div style={{ color: '#6b7280' }}>No token metadata available.</div>}
      </Section>

      {/* Top users */}
      <Section title="Most active users">
        {data?.topUsers?.length ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Conversations</th>
                  <th>Messages</th>
                </tr>
              </thead>
              <tbody>
                {data.topUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <Link className="link" to={`/assistant?userId=${u.id}`}>
                        {[u.name, u.surname].filter(Boolean).join(' ') || 'Anonymous'}
                      </Link>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{u.email || `user #${u.id}`}</div>
                    </td>
                    <td>{fmtInt(u.conversations)}</td>
                    <td>{fmtInt(u.messages)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div style={{ color: '#6b7280' }}>No data.</div>}
      </Section>
    </div>
  );
};

export default AssistantInsights;
